import React, { useState, useEffect } from "react";
import { MapContainer, TileLayer, Marker, Polyline, useMapEvents } from "react-leaflet";
import L from "leaflet";
import AdminPanel from "../panel/AdminPanel";
import "leaflet/dist/leaflet.css";
import "../styles/Transport.css";
import api from "../../../api/api";

// Remove default Leaflet icon
delete L.Icon.Default.prototype._getIconUrl;

// Bus icon
const busIcon = new L.DivIcon({
  html: "🚍",
  className: "custom-bus-icon",
  iconSize: [30, 30],
  iconAnchor: [15, 15],
});

// Map click handler
const ClickableMap = ({ stops, setStops }) => {
  useMapEvents({
    click(e) {
      const name = prompt("Enter stop name:");
      if (!name) return;

      setStops([
        ...stops,
        {
          name,
          lat: e.latlng.lat,
          lng: e.latlng.lng,
          arrival: "",
          departure: "",
        },
      ]);
    },
  });

  return null;
};

const AdminTransportMap = () => {
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);

  const [buses, setBuses] = useState([
    {
      number: "Demo Bus 101",
      drivers: ["Mr. Ramesh Kumar", "Ms. Sunita Verma"],
      stopages: [
        {
          name: "Central Station",
          lat: 28.7041,
          lng: 77.1025,
          arrival: "6:30 AM",
          departure: "6:35 AM",
        },
        {
          name: "Green Park",
          lat: 28.71,
          lng: 77.108,
          arrival: "6:50 AM",
          departure: "6:55 AM",
        },
        {
          name: "Riverside",
          lat: 28.718,
          lng: 77.115,
          arrival: "7:10 AM",
          departure: "7:15 AM",
        },
        {
          name: "Downtown",
          lat: 28.725,
          lng: 77.12,
          arrival: "7:30 AM",
          departure: "7:35 AM",
        },
      ],
    },
  ]);

  const [selectedBusIndex, setSelectedBusIndex] = useState(null);

  const [busNumber, setBusNumber] = useState("");
  const [drivers, setDrivers] = useState([""]);
  const [routeStops, setRouteStops] = useState([]);

  // Fetch buses from backend
  useEffect(() => {
    api
      .get("/api/buses")
      .then((res) => {
        setBuses(Array.isArray(res.data) ? res.data : []);
      })
      .catch((err) => console.error("Error loading buses:", err));
  }, []);

  // Add bus
  const handleAddBus = () => {
    if (!busNumber || drivers.length === 0 || routeStops.length === 0) return;

    const newBus = {
      number: busNumber,
      drivers: drivers.filter((d) => d.trim() !== ""),
      stopages: routeStops.map((stop) => ({ ...stop })),
    };

    setBuses([...buses, newBus]);
    resetForm();
  };

  // Update bus
  const handleUpdateBus = () => {
    if (selectedBusIndex === null) return;

    const updatedBuses = [...buses];

    updatedBuses[selectedBusIndex] = {
      number: busNumber,
      drivers: drivers.filter((d) => d.trim() !== ""),
      stopages: routeStops.map((stop) => ({ ...stop })),
    };

    setBuses(updatedBuses);
    resetForm();
    setSelectedBusIndex(null);
  };

  const resetForm = () => {
    setBusNumber("");
    setDrivers([""]);
    setRouteStops([]);
  };

  const handleEditBus = (index) => {
    const bus = buses[index];

    setSelectedBusIndex(index);
    setBusNumber(bus.number);
    setDrivers(bus.drivers);
    setRouteStops(bus.stopages);

    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleDragStop = (index, e) => {
    const updatedStops = [...routeStops];

    updatedStops[index] = {
      ...updatedStops[index],
      lat: e.latlng.lat,
      lng: e.latlng.lng,
    };

    setRouteStops(updatedStops);
  };

  const handleStopChange = (index, field, value) => {
    const updatedStops = [...routeStops];

    updatedStops[index][field] = value;

    setRouteStops(updatedStops);
  };

  const handleDriverChange = (index, value) => {
    const updatedDrivers = [...drivers];

    updatedDrivers[index] = value;

    setDrivers(updatedDrivers);
  };

  const addDriverField = () => setDrivers([...drivers, ""]);

  const removeDriverField = (index) =>
    setDrivers(drivers.filter((_, i) => i !== index));

  return (
    <>
      <AdminPanel onToggle={setIsSidebarCollapsed} />

      <div
        className={`transport-content ${
          isSidebarCollapsed ? "collapsed" : ""
        }`}
      >
        <h1>Admin Transport Management</h1>

        {/* FORM */}
        <div className="bus-form">
          <input
            type="text"
            placeholder="Bus Number"
            value={busNumber}
            onChange={(e) => setBusNumber(e.target.value)}
          />

          <div>
            {drivers.map((driver, idx) => (
              <div key={idx} className="stop-input">
                <input
                  type="text"
                  placeholder={`Driver ${idx + 1}`}
                  value={driver}
                  onChange={(e) =>
                    handleDriverChange(idx, e.target.value)
                  }
                />

                {drivers.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeDriverField(idx)}
                    className="add-stop-btn"
                  >
                    Remove
                  </button>
                )}
              </div>
            ))}

            <button
              type="button"
              onClick={addDriverField}
              className="add-stop-btn"
            >
              Add Driver
            </button>
          </div>

          <p>
            Click on the map below to add route stops. Drag markers to
            adjust position.
          </p>

          {selectedBusIndex === null ? (
            <button
              onClick={handleAddBus}
              className="add-bus-btn"
            >
              Save Bus
            </button>
          ) : (
            <button
              onClick={handleUpdateBus}
              className="update-bus-btn"
            >
              Update Route
            </button>
          )}
        </div>

        {/* MAP FOR ROUTE CREATION */}
        <div
          className="bus-map"
          style={{ height: "400px", marginBottom: "20px" }}
        >
          <MapContainer
            center={[28.7041, 77.1025]}
            zoom={13}
            style={{ width: "100%", height: "100%" }}
          >
            <TileLayer
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              attribution="© OpenStreetMap contributors"
            />

            <ClickableMap
              stops={routeStops}
              setStops={setRouteStops}
            />

            <Polyline
              positions={routeStops.map((s) => [s.lat, s.lng])}
              color="blue"
              weight={5}
            />

            {routeStops.map((stop, idx) => (
              <Marker
                key={idx}
                position={[stop.lat, stop.lng]}
                icon={busIcon}
                draggable
                eventHandlers={{
                  dragend: (e) => handleDragStop(idx, e),
                }}
              />
            ))}
          </MapContainer>
        </div>

        {/* SAVED BUSES */}
        {buses.map((bus, idx) => (
          <div key={idx} className="bus-card split-card">
            <div className="bus-map">
              <MapContainer
                bounds={L.latLngBounds(
                  bus.stopages.map((s) => [s.lat, s.lng])
                )}
                style={{ height: "100%", width: "100%" }}
              >
                <TileLayer
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  attribution="© OpenStreetMap contributors"
                />

                <Polyline
                  positions={bus.stopages.map((s) => [
                    s.lat,
                    s.lng,
                  ])}
                  color="blue"
                  weight={6}
                />

                {bus.stopages.map((stop, i) => (
                  <Marker
                    key={i}
                    position={[stop.lat, stop.lng]}
                    icon={busIcon}
                  />
                ))}
              </MapContainer>
            </div>

            <div className="bus-details">
              <table>
                <caption>{bus.number}</caption>
                <tbody>
                  <tr>
                    <th>Drivers</th>
                    <td>{bus.drivers.join(", ")}</td>
                  </tr>

                  <tr>
                    <th colSpan={2}>Route Stops</th>
                  </tr>

                  {bus.stopages.map((stop, i) => (
                    <tr key={i}>
                      <td>{stop.name}</td>
                      <td>
                        {stop.arrival} / {stop.departure}
                      </td>
                    </tr>
                  ))}

                  <tr>
                    <td colSpan={2}>
                      <button
                        onClick={() => handleEditBus(idx)}
                        className="edit-bus-btn"
                      >
                        Edit Route
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        ))}
      </div>
    </>
  );
};

export default AdminTransportMap;